import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { createMenuSchema, type CreateMenuFormValues } from '../schema/menuSchema';
import type { MenuTreeNode } from '../types/menu';
import { FormDialog } from '@ui-kit';
import { usePermissionCatalog } from '../hooks/usePermissionCatalog';

interface MenuCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateMenuFormValues) => void;
  isLoading: boolean;
  errorMessage?: string;
  treeNodes: MenuTreeNode[];
}

function flattenTree(nodes: MenuTreeNode[]): Array<{ id: number; label: string; depth: number }> {
  const result: Array<{ id: number; label: string; depth: number }> = [];
  function walk(list: MenuTreeNode[], d: number) {
    for (const n of list) {
      result.push({ id: n.id, label: n.nameKo, depth: d });
      if (n.children) walk(n.children, d + 1);
    }
  }
  walk(nodes, 0);
  return result;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--color-danger)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  display: 'block',
  marginBottom: 'var(--spacing-1)',
};

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginBottom: 'var(--spacing-3)',
};

const errorMsgStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 2,
};

export function MenuCreateDialog({
  open,
  onClose,
  onSubmit,
  isLoading,
  errorMessage,
  treeNodes,
}: MenuCreateDialogProps) {
  const { t } = useTranslation();
  // #471 — 권한 카탈로그 (필요 권한 드롭다운)
  const { data: permissionCatalog = [] } = usePermissionCatalog();
  const MENU_TYPES = [
    { value: 'INTERNAL', label: t('menu.form.field.menuType.INTERNAL') },
    { value: 'EXTERNAL', label: t('menu.form.field.menuType.EXTERNAL') },
    { value: 'DIVIDER', label: t('menu.form.field.menuType.DIVIDER') },
  ];
  const REQUIRED_ROLES = [
    { value: '', label: t('menu.form.field.requiredRole.placeholder') },
    { value: 'ROLE_ADMIN', label: 'ROLE_ADMIN' },
    { value: 'ROLE_MANAGER', label: 'ROLE_MANAGER' },
    { value: 'ROLE_USER', label: 'ROLE_USER' },
    { value: 'ROLE_GUEST', label: 'ROLE_GUEST' },
  ];
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateMenuFormValues>({
    resolver: zodResolver(createMenuSchema),
    defaultValues: {
      menuKey: '',
      nameKo: '',
      nameEn: '',
      menuType: 'INTERNAL',
      sortOrder: 10,
      visible: true,
      requiredPermission: null,
    },
  });

  const flatParents = flattenTree(treeNodes);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') handleClose();
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      title={t('menu.form.title.create')}
      onClose={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={isLoading ? t('menu.form.button.loading.create') : t('menu.form.button.create')}
      cancelLabel={t('menu.form.button.cancel')}
    >
      <div onKeyDown={handleKeyDown}>
        {/* Error */}
        {errorMessage && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 8%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {errorMessage}
          </div>
        )}

        <div style={fieldStyle}>
          <label htmlFor="create-menuKey" style={labelStyle}>
            {t('menu.form.field.menuKey')} *
          </label>
          <input
            id="create-menuKey"
            type="text"
            {...register('menuKey')}
            aria-invalid={!!errors.menuKey}
            aria-describedby={errors.menuKey ? 'err-create-menuKey' : undefined}
            style={errors.menuKey ? inputErrorStyle : inputStyle}
            placeholder={t('menu.form.field.menuKey.placeholder')}
            autoFocus
          />
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
            {t('menu.form.field.menuKey.hint')}
          </span>
          {errors.menuKey && (
            <span id="err-create-menuKey" role="alert" style={errorMsgStyle}>
              {errors.menuKey.message}
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-nameKo" style={labelStyle}>
            {t('menu.form.field.nameKo')} *
          </label>
          <input
            id="create-nameKo"
            type="text"
            {...register('nameKo')}
            aria-invalid={!!errors.nameKo}
            style={errors.nameKo ? inputErrorStyle : inputStyle}
            placeholder={t('menu.form.field.nameKo.placeholder')}
          />
          {errors.nameKo && (
            <span role="alert" style={errorMsgStyle}>
              {errors.nameKo.message}
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-nameEn" style={labelStyle}>
            {t('menu.form.field.nameEn')}
          </label>
          <input
            id="create-nameEn"
            type="text"
            {...register('nameEn')}
            style={inputStyle}
            placeholder="English menu name"
          />
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-menuType" style={labelStyle}>
            {t('menu.form.field.menuType')} *
          </label>
          <select
            id="create-menuType"
            {...register('menuType')}
            aria-invalid={!!errors.menuType}
            style={errors.menuType ? inputErrorStyle : inputStyle}
          >
            {MENU_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {errors.menuType && (
            <span role="alert" style={errorMsgStyle}>
              {errors.menuType.message}
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-url" style={labelStyle}>
            {t('menu.form.field.url')}
          </label>
          <input
            id="create-url"
            type="text"
            {...register('url')}
            aria-invalid={!!errors.url}
            style={errors.url ? inputErrorStyle : inputStyle}
            placeholder={t('menu.form.field.url.placeholder')}
          />
          {errors.url && (
            <span role="alert" style={errorMsgStyle}>
              {errors.url.message}
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-icon" style={labelStyle}>
            {t('menu.form.field.icon')}
          </label>
          <input
            id="create-icon"
            type="text"
            {...register('icon')}
            style={inputStyle}
            placeholder={t('menu.form.field.icon.placeholder')}
          />
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-parent" style={labelStyle}>
            {t('menu.form.field.parent')}
          </label>
          <select
            id="create-parent"
            style={inputStyle}
            {...register('parentId', { setValueAs: (v) => (v === '' ? null : Number(v)) })}
          >
            <option value="">{t('menu.form.field.parent.root')}</option>
            {flatParents.map((p) => (
              <option key={p.id} value={p.id}>
                {'  '.repeat(p.depth)}{p.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
          <div style={fieldStyle}>
            <label htmlFor="create-sortOrder" style={labelStyle}>
              {t('menu.form.field.sortOrder')}
            </label>
            <input
              id="create-sortOrder"
              type="number"
              min={0}
              max={99999}
              {...register('sortOrder', { valueAsNumber: true })}
              aria-invalid={!!errors.sortOrder}
              style={errors.sortOrder ? inputErrorStyle : inputStyle}
              placeholder="10"
            />
          </div>

          <div style={fieldStyle}>
            <label htmlFor="create-visible" style={labelStyle}>
              {t('menu.form.field.visible')}
            </label>
            <select
              id="create-visible"
              style={inputStyle}
              {...register('visible', { setValueAs: (v) => v === 'true' })}
            >
              <option value="true">{t('menu.form.field.visible.true')}</option>
              <option value="false">{t('menu.form.field.visible.false')}</option>
            </select>
          </div>
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-requiredRole" style={labelStyle}>
            {t('menu.form.field.requiredRole')}
          </label>
          <select
            id="create-requiredRole"
            style={inputStyle}
            {...register('requiredRole')}
          >
            {REQUIRED_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {/* #471 — 필요 권한 (requiredPermission) */}
        <div style={fieldStyle}>
          <label htmlFor="create-requiredPermission" style={labelStyle}>
            {t('menu.form.field.requiredPermission')}
          </label>
          <select
            id="create-requiredPermission"
            style={inputStyle}
            aria-describedby="create-hint-requiredPermission"
            {...register('requiredPermission', {
              setValueAs: (v: string) => (v === '' ? null : v),
            })}
          >
            <option value="">{t('menu.form.field.requiredPermission.none')}</option>
            {permissionCatalog.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nameKo} ({p.code})
              </option>
            ))}
          </select>
          <span
            id="create-hint-requiredPermission"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              marginTop: 'var(--spacing-1)',
            }}
          >
            {t('menu.form.field.requiredPermission.helper')}
          </span>
        </div>

        <div style={fieldStyle}>
          <label htmlFor="create-description" style={labelStyle}>
            {t('menu.form.field.description')}
          </label>
          <textarea
            id="create-description"
            rows={2}
            {...register('description')}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 56,
            }}
            placeholder={t('menu.form.field.description.placeholder')}
          />
        </div>
      </div>
    </FormDialog>
  );
}
